import * as React from 'react';
import { ReactComponent as CloseGrey } from '../../assets/close-grey.svg';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import Dropdown, { DropdownOption } from '../Dropdown';
import { CopyButton } from '../CopyButton';
import { copyToClipboard } from '../utils';
import {
  SSO_AUTH_METHOD,
  SsoConfigDetail,
  SsoConfigPayload,
} from '../../model/ssoConfigPayload';
import { SaveSsoConfigInfo } from '../../reducers/ssoConfigReducer';

export enum SSO_MODAL_MODE {
  CREATE = 'create',
  EDIT = 'edit',
}

type Props = {
  mode: SSO_MODAL_MODE;
  config: SsoConfigDetail | null;
  partnerId: number;
  saveConfigDetails: SaveSsoConfigInfo;
  saveSsoConfig: (payload: SsoConfigPayload, isEdit: boolean) => void;
  closeModal: (shouldRefetch: boolean) => void;
} & WrappedComponentProps;

interface State {
  domain: string;
  issuerUrl: string;
  clientId: string;
  authMethod: string;
  clientSecret: string;
}

/**
 * `private_key_jwt` is the only auth method supported by the panel for now.
 * `client_secret_post` and `none` are listed but disabled: the "get configs" API
 * masks `clientSecret` as null, so an existing secret-based config cannot be
 * edited without re-entering a secret the admin was never shown. Re-enable once
 * the edit flow supports "keep existing secret".
 */
const AUTH_METHOD_LIST: DropdownOption[] = [
  { label: SSO_AUTH_METHOD.PRIVATE_KEY_JWT, value: SSO_AUTH_METHOD.PRIVATE_KEY_JWT },
  {
    label: SSO_AUTH_METHOD.CLIENT_SECRET_POST,
    value: SSO_AUTH_METHOD.CLIENT_SECRET_POST,
    isDisabled: true,
  },
  { label: SSO_AUTH_METHOD.NONE, value: SSO_AUTH_METHOD.NONE, isDisabled: true },
];

const DOMAIN_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

class SsoConfigModal extends React.Component<Props> {
  state: State =
    this.props.mode === SSO_MODAL_MODE.EDIT && this.props.config
      ? {
          domain: this.props.config.domain,
          issuerUrl: this.props.config.issuerUrl,
          clientId: this.props.config.clientId,
          authMethod: this.props.config.authMethod,
          clientSecret: this.props.config.clientSecret || '',
        }
      : {
          domain: '',
          issuerUrl: '',
          clientId: '',
          authMethod: '',
          clientSecret: '',
        };

  handleFieldChange = (field: keyof State) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.setState({ [field]: e.target.value });
  };

  handleAuthMethodClick = (item: DropdownOption) => {
    this.setState({ authMethod: item.value });
  };

  isFormValid = () => {
    const { domain, issuerUrl, clientId, authMethod, clientSecret } = this.state;
    if (!DOMAIN_REGEX.test(domain.trim())) return false;
    if (!issuerUrl.trim().startsWith('https://')) return false;
    if (!clientId.trim()) return false;
    if (!authMethod) return false;
    if (authMethod === SSO_AUTH_METHOD.CLIENT_SECRET_POST && !clientSecret.trim())
      return false;
    return true;
  };

  handleSubmit = () => {
    const { domain, issuerUrl, clientId, authMethod, clientSecret } = this.state;
    const isEditMode = this.props.mode === SSO_MODAL_MODE.EDIT;
    const payload: SsoConfigPayload = {
      domain: domain.trim().toLowerCase(),
      partnerId: this.props.partnerId,
      issuerUrl: issuerUrl.trim().replace(/\/+$/, ''),
      clientId: clientId.trim(),
      authMethod: authMethod,
      clientSecret:
        !isEditMode && authMethod === SSO_AUTH_METHOD.CLIENT_SECRET_POST
          ? clientSecret.trim()
          : null,
    };
    this.props.saveSsoConfig(payload, isEditMode);
  };

  handleCloseClick = () => {
    this.props.closeModal(
      this.props.saveConfigDetails.isSaveConfigRequestSuccess != null
    );
  };

  getSuccessContent = () => {
    const saveResponse = this.props.saveConfigDetails.isSaveConfigRequestSuccess;
    if (saveResponse == null) return null;
    return (
      <div className="sso-config-modal-container">
        <div className="sso-config-modal-heading">
          <FormattedMessage id="sso-config-success-heading" />
        </div>
        <div className="sso-config-success-instruction">
          <FormattedMessage id="sso-config-success-instruction" />
        </div>
        <div className="sso-config-copy-label">
          <FormattedMessage id="sso-config-redirect-url-label" />
        </div>
        <div className="sso-config-copy-row">
          <div className="sso-config-copy-value">{saveResponse.redirectUrl}</div>
          <CopyButton onClick={() => copyToClipboard(saveResponse.redirectUrl)} />
        </div>
        {this.state.authMethod === SSO_AUTH_METHOD.PRIVATE_KEY_JWT && (
          <div>
            <div className="sso-config-copy-label">
              <FormattedMessage id="sso-config-jwks-url-label" />
            </div>
            <div className="sso-config-copy-row">
              <div className="sso-config-copy-value">{saveResponse.jwksUrl}</div>
              <CopyButton onClick={() => copyToClipboard(saveResponse.jwksUrl)} />
            </div>
          </div>
        )}
        <div className="create-user-button-container">
          <button
            className="button button-primary create-user-button"
            onClick={() => this.props.closeModal(true)}
          >
            <FormattedMessage id="done" />
          </button>
        </div>
      </div>
    );
  };

  getFormContent = () => {
    const isSaving = this.props.saveConfigDetails.isSaveConfigRequestInProgress;
    const error = this.props.saveConfigDetails.saveConfigRequestError;
    const selectedAuthMethodIndex = AUTH_METHOD_LIST.findIndex(
      (item) => item.value === this.state.authMethod
    );
    const buttonText = isSaving ? (
      <div className="modal-button-div">
        <div className="modal-spinner" />
        <FormattedMessage id="sso-config-saving" />
      </div>
    ) : (
      <FormattedMessage id="sso-config-save" />
    );
    return (
      <div className="sso-config-modal-container">
        <div className="sso-config-modal-heading">
          {this.props.mode === SSO_MODAL_MODE.CREATE ? (
            <FormattedMessage id="sso-config-add-heading" />
          ) : (
            <FormattedMessage id="sso-config-edit-heading" />
          )}
        </div>
        <div className="input-box-heading">
          <FormattedMessage id="sso-config-domain" />
          <div className="input input-box input-div">
            <input
              className="no-border-input"
              placeholder={this.props.intl.formatMessage({
                id: 'sso-config-domain-placeholder',
              })}
              value={this.state.domain}
              disabled={this.props.mode === SSO_MODAL_MODE.EDIT}
              onChange={this.handleFieldChange('domain')}
            />
          </div>
        </div>
        <div className="input-box-heading">
          <FormattedMessage id="sso-config-issuer-url" />
          <div className="input input-box input-div">
            <input
              className="no-border-input"
              placeholder={this.props.intl.formatMessage({
                id: 'sso-config-issuer-placeholder',
              })}
              value={this.state.issuerUrl}
              onChange={this.handleFieldChange('issuerUrl')}
            />
          </div>
        </div>
        <div className="input-box-heading">
          <FormattedMessage id="sso-config-client-id" />
          <div className="input input-box input-div">
            <input
              className="no-border-input"
              placeholder={this.props.intl.formatMessage({
                id: 'sso-config-client-id-placeholder',
              })}
              value={this.state.clientId}
              onChange={this.handleFieldChange('clientId')}
            />
          </div>
        </div>
        <div className="input-box-heading">
          <FormattedMessage id="sso-config-auth-method" />
          <Dropdown
            mainClass="dropdown-container"
            headerClass="dropdown-header"
            headerTitleClass="dropdown-header-title"
            listContainerClass="dropdown-list-container"
            dropDownItemClass="role-dropdown-menu-item"
            onItemClick={this.handleAuthMethodClick}
            defaultItemSelectedIndexes={
              selectedAuthMethodIndex !== -1 ? [selectedAuthMethodIndex] : null
            }
            placeholderText={this.props.intl.formatMessage({
              id: 'sso-config-auth-method-placeholder',
            })}
            placeholderClass="partner-dropdown-placeholder"
            shouldAllowMultiSelect={false}
            list={AUTH_METHOD_LIST}
          />
        </div>
        {this.state.authMethod === SSO_AUTH_METHOD.CLIENT_SECRET_POST && (
          <div className="input-box-heading">
            <FormattedMessage id="sso-config-client-secret" />
            <div className="input input-box input-div">
              <input
                className="no-border-input"
                type="password"
                placeholder={this.props.intl.formatMessage({
                  id: 'sso-config-client-secret-placeholder',
                })}
                value={this.state.clientSecret}
                onChange={this.handleFieldChange('clientSecret')}
              />
            </div>
          </div>
        )}
        <div className="sso-config-save-warning">
          <FormattedMessage id="sso-config-save-warning" />
        </div>
        <div className="create-user-button-container">
          <button
            className="button button-primary create-user-button"
            disabled={!this.isFormValid() || isSaving}
            onClick={this.handleSubmit}
          >
            {buttonText}
          </button>
          <button
            className="button create-user-button modal-cancel-button"
            onClick={this.handleCloseClick}
          >
            <FormattedMessage id="cancel" />
          </button>
        </div>
        {error ? <div className="create-user-error">{error}</div> : ''}
      </div>
    );
  };

  render() {
    const isSaved =
      this.props.saveConfigDetails.isSaveConfigRequestSuccess != null;
    return (
      <div>
        <CloseGrey
          className="alert-modal-close-button"
          onClick={this.handleCloseClick}
        />
        {isSaved ? this.getSuccessContent() : this.getFormContent()}
      </div>
    );
  }
}

export default injectIntl(SsoConfigModal);
